// Page worlds: every public page as a scroll-driven 3D avenue. A world is
// the page's sections in order, each one a station with a camera pose and
// (usually) a set piece standing there. The words stay in the DOM exactly
// as they are; the world is what you see behind and around them, and what
// you walk through in Explore. See components/site/PageWorld.js.
//
// A station:
//   at     where the section is in the page: '#id', 'header', 'footer',
//          or 'section:N' (the Nth .cs-store-section / .ak-section)
//   label  the chip (1–9 fly there in Explore)
//   set    { kind, scale?, side? } — a set piece from components/site/setPieces.js
//          kind: hero | split | eco | gate | ledger | rack | chassis | ring |
//                city | beacon | doc | board | film
//
// Stations line up along +x, SPACING apart, swinging a little left and
// right so the avenue bends; set pieces stand on the side the copy leaves
// open (`side`: -1 left, 1 right, 0 centre).
export const SPACING = 13;

// The piece stands PIECE_X to its side of the station; the camera frames
// the station centre from front-above, so the piece lands in the outer
// third of the screen and the copy column over the centre stays readable.
// Centre pieces (side 0) sit behind and below the copy instead.
const PIECE_X = 5.2;

function pose(x, z, side, scale = 1) {
  const tx = x + side * 1.0;
  return { position: [x - side * 0.8, 2.6 + scale * 0.4, z + 10 + scale * 1.8], target: [tx, 0.5 + scale * 0.3, z] };
}

export function avenue(key, steps) {
  const stations = steps.map((st, i) => {
    const x = i * SPACING;
    const z = Math.sin(i * 1.1) * 2.6;
    const side = st.set?.side ?? (i % 2 ? 1 : -1);
    const scale = st.set?.scale ?? 1;
    return {
      id: st.at.replace(/^#/, '').replace(':', '-'),
      at: st.at,
      label: st.label,
      pose: pose(x, z, side, scale),
      set: st.set ? { ...st.set, side, scale, position: [x + side * PIECE_X, 0, z - (side ? 1.2 : 4.5)] } : null,
      point: [x, 0, z],
    };
  });
  return { key, stations };
}

// Sections the landing has carried (stations whose section is absent on a
// given build are simply skipped, so old and new orderings both work).
export const LANDING = avenue('landing', [
  { at: 'header', label: 'the bridge', set: { kind: 'hero', scale: 0.9, side: 1 } },
  { at: '#measured', label: 'measured', set: { kind: 'board', side: -1 } },
  { at: '#jobs', label: 'the jobs', set: { kind: 'rack', side: 1 } },
  { at: '#proof', label: 'four moves', set: { kind: 'gate', side: -1 } },
  { at: '#proofs', label: 'proofs', set: { kind: 'ledger', side: 1 } },
  { at: '#works-with', label: 'works with', set: { kind: 'rack', side: -1 } },
  { at: '#products', label: 'two products', set: { kind: 'split', scale: 0.85, side: 1 } },
  { at: '#offer', label: 'the offer', set: { kind: 'doc', side: -1 } },
  { at: '#ecosystem', label: 'the fleet', set: { kind: 'eco', scale: 0.9, side: -1 } },
  { at: '#modules', label: 'modules', set: { kind: 'city', side: 1 } },
  { at: '#whats-new', label: "what's new", set: { kind: 'doc', side: -1 } },
  { at: '#start', label: 'start', set: { kind: 'chassis', side: 1 } },
  { at: 'footer', label: 'nventr', set: { kind: 'beacon', side: 0 } },
]);

export const PLATFORM = avenue('platform', [
  { at: 'header', label: 'platform', set: { kind: 'gate', side: 1 } },
  { at: '#invoice', label: 'the invoice', set: { kind: 'ledger', side: -1 } },
  { at: '#design', label: 'design', set: { kind: 'board', side: 1 } },
  { at: '#agents', label: 'agents', set: { kind: 'rack', side: 1 } },
  { at: '#modules', label: 'modules', set: { kind: 'city', side: -1 } },
  { at: '#app-kit', label: 'app kit', set: { kind: 'chassis', side: 1 } },
  { at: '#built-on', label: 'built on', set: { kind: 'ledger', side: -1 } },
]);

// One template for the nine /platform/:slug pages: hero stage, the
// walkthrough sections, the coordinator, the closing band.
export const MODULE = avenue('module', [
  { at: 'header', label: 'the module', set: { kind: 'board', side: 1 } },
  { at: 'section:1', label: 'what it does', set: { kind: 'gate', side: -1 } },
  { at: 'section:3', label: 'how it runs', set: { kind: 'rack', side: 1 } },
  { at: '#coordinator', label: 'coordinator', set: { kind: 'ring', side: -1 } },
  { at: 'section:7', label: 'next', set: { kind: 'chassis', side: 1 } },
]);

export const APP_KIT = avenue('app-kit', [
  { at: 'header', label: 'app kit', set: { kind: 'chassis', scale: 1.1, side: 1 } },
  { at: '#beside-platform', label: 'beside platform', set: { kind: 'split', scale: 0.7, side: -1 } },
  { at: '#dev-console', label: 'dev console', set: { kind: 'board', side: 1 } },
  { at: '#foundation', label: 'foundation', set: { kind: 'rack', side: 1 } },
  { at: '#assurance', label: 'assurance', set: { kind: 'ring', side: -1 } },
]);

export const PRICING = avenue('pricing', [
  { at: 'header', label: 'pricing', set: { kind: 'doc', side: 1 } },
  { at: '#platform', label: 'platform', set: { kind: 'gate', side: -1 } },
  { at: '#kit', label: 'app kit', set: { kind: 'chassis', side: 1 } },
  { at: '#native', label: 'native', set: { kind: 'board', side: -1 } },
  { at: '#lab', label: 'lab', set: { kind: 'ring', side: 1 } },
  { at: '#bundle', label: 'bundle', set: { kind: 'split', scale: 0.7, side: -1 } },
  { at: '#enterprise', label: 'enterprise', set: { kind: 'city', side: 1 } },
]);

export const STORE = avenue('store', [
  { at: 'header', label: 'hardware', set: { kind: 'city', side: 1 } },
  { at: 'section:1', label: 'the boxes', set: { kind: 'rack', side: -1 } },
  { at: 'footer', label: 'order', set: { kind: 'beacon', side: 0 } },
]);

export const PARTNERS = avenue('partners', [
  { at: 'header', label: 'partners', set: { kind: 'city', side: 1 } },
  { at: 'section:1', label: 'the model', set: { kind: 'gate', side: -1 } },
  { at: '#earnings', label: 'earnings', set: { kind: 'ledger', side: 1 } },
  { at: 'footer', label: 'talk', set: { kind: 'beacon', side: 0 } },
]);

export const PHYSICAL = avenue('physical', [
  { at: 'header', label: 'gridos', set: { kind: 'city', side: 1 } },
  { at: 'section:1', label: 'control plane', set: { kind: 'ring', side: -1 } },
]);

export const GATE = avenue('gate', [
  { at: 'header', label: 'the gate', set: { kind: 'gate', scale: 1.2, side: 1 } },
  { at: 'section:1', label: 'approval', set: { kind: 'ledger', side: -1 } },
  { at: '#routing', label: 'routing', set: { kind: 'rack', side: 1 } },
]);

export const WATCH = avenue('watch', [
  { at: 'header', label: 'the film', set: { kind: 'film', side: 0 } },
  { at: '#bridge', label: 'bridge', set: { kind: 'hero', scale: 0.7, side: 1 } },
  { at: '#platform', label: 'platform', set: { kind: 'gate', side: -1 } },
  { at: '#govern', label: 'govern', set: { kind: 'ledger', side: 1 } },
  { at: '#kit', label: 'app kit', set: { kind: 'chassis', side: -1 } },
  { at: '#brain', label: 'one brain', set: { kind: 'ring', side: 1 } },
  { at: '#results', label: 'results', set: { kind: 'board', side: -1 } },
  { at: '#start', label: 'start', set: { kind: 'beacon', side: 0 } },
]);

export const SUPPORT = avenue('support', [
  { at: 'header', label: 'support', set: { kind: 'beacon', side: 1 } },
  { at: 'section:1', label: 'your request', set: { kind: 'doc', side: -1 } },
]);

export const SECURITY = avenue('security', [
  { at: 'header', label: 'security', set: { kind: 'ring', scale: 1.1, side: 1 } },
  { at: 'section:1', label: 'the boundary', set: { kind: 'gate', side: -1 } },
]);
